// Query for getting a single organization, typically a Zone/Unit
import gql from "graphql-tag";
import {
  accountFields,
  deviceFields,
  enrollmentInfoFields,
  organizationFields,
  userFields,
  incidentFields,
} from "../../graphql/fragments";

export const SMART_UNITS = gql`
  query SmartUnits($id: ID!) {
    smartUnits: organizationDescendants(
      id: $id
      assigned: true
      type: "zone"
      subtype: "unit"
    ) {
      ...OrganizationFields
      scheduled
      scheduledMoveInAt
      parent {
        id
        name
        subtype
      }
      enrollmentInfo {
        ...EnrollmentInfoFields
      }
      devices {
        ...DeviceFields
      }
      billingAccounts(type: "consumer") {
        ...AccountFields
        users {
          id
          firstName
          lastName
        }
      }
      incidents(sort: "closedAt desc", authorized: false, limit: 1) {
        id
        status
        resolution
      }
      responders {
        escalationLevel
        user {
          id
          firstName
          lastName
        }
      }
    }
  }
  ${accountFields}
  ${deviceFields}
  ${enrollmentInfoFields}
  ${organizationFields}
`;

export const ORGANIZATION_DEVICES = gql`
  query OrganizationDevices(
    $id: ID!
    $searchText: String
    $status: String
    $dispositions: [String]
  ) {
    organizationDevices(
      id: $id
      searchText: $searchText
      deviceSubtypes: ["defender", "water_sensor", "door_sensor"]
      status: $status
      dispositions: $dispositions
    ) {
      ...DeviceFields
      assignmentHistory {
        organizationId
        organizationName
        timestamp
      }
      organization {
        id
      }
    }
  }
  ${deviceFields}
`;

export const INVENTORY_SMART_UNITS = gql`
  query InventorySmartUnits($id: ID!, $searchText: String) {
    inventorySmartUnits: organizationDescendants(
      id: $id
      assigned: false
      type: "zone"
      subtype: "unit"
      isDemo: false
      searchText: $searchText
    ) {
      ...OrganizationFields
      responders {
        id
        demoEndingAt
        status
        user {
          id
          firstName
          lastName
        }
      }
      devices {
        ...DeviceFields
        assignmentHistory {
          organizationId
          organizationName
          timestamp
        }
        organization {
          id
        }
      }
    }
  }
  ${organizationFields}
  ${deviceFields}
`;

export const ENDED_SERVICE_SMART_UNITS = gql`
  query EndedServiceSmartUnits($id: ID!, $searchText: String) {
    endedServiceSmartUnits: organizationDescendants(
      id: $id
      accountStatus: "ended_service"
      type: "zone"
      subtype: "unit"
      searchText: $searchText
    ) {
      ...OrganizationFields
      responders {
        id
        status
        user {
          id
          firstName
          lastName
        }
      }
      devices {
        ...DeviceFields
        assignmentHistory {
          organizationId
          organizationName
          timestamp
        }
        organization {
          id
        }
      }
    }
  }
  ${organizationFields}
  ${deviceFields}
`;

// We're using this to check for existing zones/units and whether the unit
// is currently "assigned" and has devices, to inform the UI/UX.
export const SEARCH_EXISTING_UNITS = gql`
  query SearchExistingUnits(
    $id: ID!
    $searchText: String!
    $assigned: Boolean
    $limit: Int
  ) {
    searchExistingUnits: organizationDescendants(
      id: $id
      searchText: $searchText
      type: "zone"
      assigned: $assigned
      isDemo: false
      limit: $limit
      sort: "name"
    ) {
      ...OrganizationFields
      devices {
        ...DeviceFields
      }
      responders {
        id
        escalationLevel
        user {
          ...UserFields
        }
      }
    }
  }
  ${deviceFields}
  ${organizationFields}
  ${userFields}
`;

export const GET_SMART_UNIT = gql`
  query GetSmartUnit($id: ID!, $expanded: Boolean) {
    organization(id: $id, expanded: $expanded) {
      ...OrganizationFields
      parent {
        id
        name
        subtype
      }
      enrollmentInfo {
        ...EnrollmentInfoFields
      }
      devices {
        ...DeviceFields
      }
      billingAccounts(type: "consumer") {
        id
        name
        status
        type
      }
      responders {
        id
        escalationLevel
        demoEndingAt
        contactMethod {
          id
          type
          value
        }
        user {
          id
          firstName
          lastName
        }
      }
      organizationDetails {
        description
        unitAmenities
        unitDesign
        entryLocation
        doorType
      }
    }
  }
  ${deviceFields}
  ${enrollmentInfoFields}
  ${organizationFields}
`;

export const AWAITING_DEVICE_UNITS = gql`
  query AwaitingDeviceUnits($id: ID!) {
    smartUnits: organizationDescendants(
      id: $id
      assigned: true
      type: "zone"
      subtype: "unit"
    ) {
      id
      name
      unitType {
        name
        width
        length
      }
      enrollmentInfo {
        ...EnrollmentInfoFields
      }
      responders {
        user {
          id
          firstName
          lastName
        }
        contactMethod {
          type
          value
        }
      }
    }
  }
  ${enrollmentInfoFields}
`;

export const MISSING_DEVICE_SMART_UNITS_COUNT = gql`
  query MissingDeviceUnitsCount($id: ID!) {
    smartUnits: organizationDescendants(
      id: $id
      assigned: true
      device_assigned: false
      type: "zone"
      subtype: "unit"
    ) {
      id
      enrollmentInfo {
        status
      }
    }
  }
`;

export const MISSING_DEVICE_UNITS = gql`
  query MissingDeviceUnits($id: ID!) {
    smartUnits: organizationDescendants(
      id: $id
      assigned: true
      device_assigned: false
      type: "zone"
      subtype: "unit"
    ) {
      id
      name
      unitType {
        name
        width
        length
      }
      enrollmentInfo {
        ...EnrollmentInfoFields
      }
      billingAccounts(type: "consumer") {
        id
        name
        status
        type
        insertedAt
        users {
          id
          firstName
          lastName
        }
      }
      responders {
        user {
          id
          firstName
          lastName
        }
        contactMethod {
          type
          value
        }
      }
    }
  }
  ${enrollmentInfoFields}
`;

export const UPDATE_SMART_UNIT = gql`
  mutation UpdateSmartUnit(
    $id: ID!
    $name: String!
    $unitTypeId: ID
    $location: InputLocation
  ) {
    updateSmartUnit(
      id: $id
      name: $name
      unitTypeId: $unitTypeId
      location: $location
    ) {
      ...OrganizationFields
    }
  }
  ${organizationFields}
`;

export const GET_DEVICE = gql`
  query GetDevice($id: ID!) {
    device(id: $id) {
      ...DeviceFields
      organization {
        ...OrganizationFields
      }
    }
  }
  ${deviceFields}
  ${organizationFields}
`;

export const UPDATE_DEVICE = gql`
  mutation UpdateDevice(
    $id: ID!
    $externalId: String!
    $disposition: String
    $organizationId: ID!
    $type: String!
    $specs: InputDeviceSpec
    $dateReportedMissing: Date
  ) {
    updateDevice(
      id: $id
      disposition: $disposition
      externalId: $externalId
      organizationId: $organizationId
      type: $type
      specs: $specs
      dateReportedMissing: $dateReportedMissing
    ) {
      ...DeviceFields
      organization {
        ...OrganizationFields
      }
    }
  }
  ${deviceFields}
  ${organizationFields}
`;

export const SMART_UNIT_INCIDENTS = gql`
  query SmartUnitIncidents(
    $organizationId: ID
    $parentId: ID
    $dateTimeFilter: DateTimeFilter!
    $sort: String
    $type: String
    $assigned: Boolean
  ) {
    incidents(
      organizationId: $organizationId
      parentId: $parentId
      dateTimeFilter: $dateTimeFilter
      sort: $sort
      type: $type
      assigned: $assigned
    ) {
      ...IncidentFields
      devices {
        id
        externalId
        name
      }
      organization {
        id
        name
      }
    }
  }
  ${incidentFields}
`;

// query to get a timestamp for 'organization_added' (start renting) event
export const USER_BILLING_ACCOUNT_EVENTS = gql`
  query UserBillingAccountEvents($organizationId: ID!, $renterId: ID!) {
    userBillingAccounts(organizationId: $organizationId, userId: $renterId) {
      billingAccount {
        events(sort: "timestamp desc", types: ["billing_account"]) {
          data
          name
          timestamp
        }
      }
    }
  }
`;

export const UPDATE_UNIT_DETAILS = gql`
  mutation UpdateUnitDetails(
    $organizationId: ID!
    $description: String
    $unitDesign: [String]
    $unitAmenities: [String]
  ) {
    updateOrganizationDetails(
      organizationId: $organizationId
      description: $description
      unitDesign: $unitDesign
      unitAmenities: $unitAmenities
    ) {
      description
      unitDesign
      unitAmenities
    }
  }
`;

export const OFFICE_INVENTORY_DEVICES = gql`
  query OfficeInventoryDevices(
    $id: ID!
    $searchText: String
    $dispositions: [String]
    $statuses: [String]
    $organizationAssigned: Boolean
    $page: Int
    $limit: Int
    $sort: String = "name asc"
    $availableInventory: Boolean
  ) {
    organizationDevicesPaginated(
      id: $id
      searchText: $searchText
      deviceSubtypes: ["defender", "water_sensor", "door_sensor"]
      dispositions: $dispositions
      statuses: $statuses
      organizationAssigned: $organizationAssigned
      page: $page
      limit: $limit
      sort: $sort

      availableInventory: $availableInventory
    ) {
      data {
        ...DeviceFields
        seqId
        assignmentHistory {
          organizationId
          organizationName
          timestamp
        }
        organization {
          id
        }
      }

      page {
        totalCount
        totalPages
        currentPage
        pageSize
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${deviceFields}
`;

export const PREINSTALLED_INVENTORY_UNITS = gql`
  query PreinstalledInventoryUnits(
    $id: ID!
    $assigned: Boolean
    $deviceAssigned: Boolean
    $type: String
    $subtype: String
    $searchText: String
    $limit: Int
    $page: Int
    $sort: String = "name asc"
  ) {
    organizationDescendantsPaginated(
      id: $id
      assigned: $assigned
      deviceAssigned: $deviceAssigned
      type: $type
      subtype: $subtype
      searchText: $searchText
      isDemo: false
      limit: $limit
      page: $page
      sort: $sort
    ) {
      data {
        ...OrganizationFields
        seqId
        responders {
          id
          demoEndingAt
        }
        devices {
          ...DeviceFields
          assignmentHistory {
            organizationId
            organizationName
            timestamp
          }
          organization {
            id
          }
        }
      }

      page {
        totalCount
        totalPages
        currentPage
        pageSize
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${organizationFields}
  ${deviceFields}
`;

export const UNAVAILABLE_INVENTORY = gql`
  query UnavailableInventory(
    $id: ID!
    $dispositions: [String]
    $statuses: [String]
    $searchText: String
    $limit: Int
    $availableInventory: Boolean
    $page: Int
    $sort: String = "name asc"
  ) {
    unavailableItems: organizationDevicesPaginated(
      id: $id
      searchText: $searchText
      deviceSubtypes: ["defender", "water_sensor", "door_sensor"]
      dispositions: $dispositions
      statuses: $statuses
      limit: $limit
      page: $page
      sort: $sort
      availableInventory: $availableInventory
    ) {
      data {
        ...DeviceFields
        seqId
        assignmentHistory {
          organizationId
          organizationName
          timestamp
        }
        organization {
          id
        }
      }
      page {
        totalCount
        totalPages
        currentPage
        pageSize
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${deviceFields}
`;

export const ORGANIZATION_INVENTORY_SUMMARY = gql`
  query OrganizationInventorySummary($ids: [ID]!) {
    organizationInventorySummary(ids: $ids) {
      facilityId
      facilityName
      organizationId
      organizationName
      regionId
      regionName
      preInstalledUnits
      officeInventory
      unhealthyDevices
      unitsMissingDevices
      totalDevices
    }
  }
`;

export const SEND_PREINSTALLED_DEVICE_MOBILE_SUMMARY = gql`
  mutation SendPreinstalledDevicesMobileSummary(
    $doneBy: String!
    $facilityId: ID!
    $emails: [String!]!
    $devicesDetails: [PreinstalledDeviceDetailInput!]!
  ) {
    sendPreinstalledDevicesMobileSummary(
      doneBy: $doneBy
      facilityId: $facilityId
      emails: $emails
      devicesDetails: $devicesDetails
    ) {
      notification
      status
    }
  }
`;

export const ASSIGN_UNIT_ZONE = gql`
  mutation AssignUnitZone($zoneId: ID!, $unitIds: [ID!]!) {
    assignUnitsToZone(zoneId: $zoneId, unitIds: $unitIds) {
      id
      name
      subtype
      parent {
        id
        name
        subtype
      }
    }
  }
`;

export const UNASSIGN_UNIT_FROM_ZONE = gql`
  mutation UnassignUnitFromZone($unitId: ID!) {
    unassignUnitFromZone(unitId: $unitId) {
      id
      name
      subtype
      parent {
        id
        name
        subtype
      }
    }
  }
`;

export const GET_FACILITY_ZONES = gql`
  query GetFacilityZones($facilityId: ID!) {
    organizationDescendants(id: $facilityId, type: "zone", subtype: "zone") {
      id
      name
      subtype
    }
  }
`;
